import { useMemo } from "react"

import { routeLoadFactor, toCapacityStop } from "@/lib/loadFactor"
import { findRouteInstanceOn } from "@/lib/plannedRouteMapping"
import {
  applyPlannedMileageMultiplier,
  calculateDistanceFromPlannedStops,
  calculateDurationMinutes,
  findVehicleType,
  priceRoute,
} from "@/lib/routeCostCalculation"
import type {
  CollectionsConfig,
  Location,
  PlannedRoute,
  RouteInstance,
  StopInstance,
  VehicleType,
} from "@/types/collections"

export function useRouteMetrics({
  filteredRoutes,
  vehicleTypes,
  config,
  locations,
  routeInstances,
  selectedDate,
  getVolumeForecastForStop,
}: {
  filteredRoutes: PlannedRoute[]
  vehicleTypes: VehicleType[] | undefined
  config: CollectionsConfig | undefined
  locations: Location[] | undefined
  routeInstances: RouteInstance[] | undefined
  selectedDate: Date
  getVolumeForecastForStop: (route: PlannedRoute, stop: { locationId?: string }) => number
}) {
  const routeCostsMap = useMemo(() => {
    const costs = new Map<string, ReturnType<typeof priceRoute>>()
    if (!vehicleTypes || !config) return costs

    filteredRoutes.forEach((route) => {
      const routeInstance = findRouteInstanceOn(routeInstances, route.id, selectedDate)
      const priceWith = (
        distanceMiles: number | null | undefined,
        durationMinutes: number | null | undefined,
      ) =>
        priceRoute({
          vehicleTypes,
          vehicleTypeRef: route.vehicleType,
          config,
          distanceMiles,
          durationMinutes,
          day: routeInstance && {
            stops: routeInstance.stops,
            crewSize: routeInstance.crew_size,
            routeKind: routeInstance.route_kind,
          },
          plan: { stops: route.stops, crewSize: route.crewSize, routeKind: route.route_kind },
        })

      let cost =
        routeInstance && routeInstance.planned_route?.vehicle_type
          ? priceWith(routeInstance.distance_miles, routeInstance.duration_minutes)
          : null

      if (!cost || (!cost.charging_cost && !cost.driver_wage_cost)) {
        const rawDistanceMiles =
          route.stops && route.stops.length >= 2
            ? calculateDistanceFromPlannedStops(route.stops)
            : null
        const distanceMiles =
          applyPlannedMileageMultiplier(rawDistanceMiles, config) ?? rawDistanceMiles
        const durationMinutes =
          route.plannedStartTime && route.plannedEndTime
            ? calculateDurationMinutes(route.plannedStartTime, route.plannedEndTime)
            : null
        cost = priceWith(distanceMiles, durationMinutes)
      }

      if (cost) costs.set(route.id, cost)
    })

    return costs
  }, [filteredRoutes, vehicleTypes, config, routeInstances, selectedDate])

  const routeLoadFactors = useMemo(() => {
    const map = new Map<string, number>()
    if (!vehicleTypes || !locations) return map

    filteredRoutes.forEach((route) => {
      const vehicleTypeName = route.vehicleType
      const vehicleType = vehicleTypeName ? findVehicleType(vehicleTypes, vehicleTypeName) : null
      if (!vehicleType) return

      const routeInstance = findRouteInstanceOn(routeInstances, route.id, selectedDate)

      // Planned stops carry zero volume until an instance exists, so fall back to the slot.
      const stopsForCapacity = routeInstance?.stops
        ? routeInstance.stops.map((stop: StopInstance) => toCapacityStop(stop))
        : (route.stops || []).map((stop) =>
            toCapacityStop(stop, {
              volume: stop.locationId ? getVolumeForecastForStop(route, stop) : 0,
            }),
          )

      const loadFactor = routeLoadFactor({
        vehicleType,
        stops: stopsForCapacity,
        locations,
        config,
      })
      if (loadFactor !== null) map.set(route.id, loadFactor)
    })
    return map
  }, [
    filteredRoutes,
    vehicleTypes,
    locations,
    config,
    routeInstances,
    selectedDate,
    getVolumeForecastForStop,
  ])

  return { routeCostsMap, routeLoadFactors }
}
